import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSimCards } from '@/hooks/useSimCards';
import { ISimCard, ISimListParams } from '@/types/api';
import SimCardItem from './SimCardItem';
import SearchBar from './SearchBar';
import Pagination from './Pagination';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import { MobileCard } from '@/components/ui';

interface SimCardListProps {
  onSelectSim?: (sim: ISimCard) => void;
}

const SimCardList: React.FC<SimCardListProps> = ({ onSelectSim }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useState<ISimListParams>({
    page: 1,
    pageSize: 25,
    q: '',
    sort: '',
  });

  const { data, isLoading, error, isFetching, refetch } = useSimCards(searchParams);

  // Memoized search handler to avoid unnecessary re-renders
  const handleSearch = useMemo(
    () => (query: string) => {
      setSearchParams(prev => ({
        ...prev,
        q: query,
        page: 1, // Reset to first page when searching
      }));
    },
    []
  );

  // Sort handler
  const handleSort = useMemo(
    () => (field: string) => {
      setSearchParams(prev => {
        const currentSort = prev.sort || '';
        let newSort = field;
        
        // Toggle between ascending and descending
        if (currentSort === field) {
          newSort = `-${field}`;
        } else if (currentSort === `-${field}`) {
          newSort = '';
        }
        
        return {
          ...prev,
          sort: newSort,
          page: 1, // Reset to first page when sorting
        };
      });
    },
    [searchParams.sort]
  );

  // Pagination handler
  const handlePageChange = useMemo(
    () => (page: number) => {
      setSearchParams(prev => ({
        ...prev,
        page,
      }));
    },
    []
  );

  // Page size handler
  const handlePageSizeChange = useMemo(
    () => (pageSize: number) => {
      setSearchParams(prev => ({
        ...prev,
        pageSize,
        page: 1, // Reset to first page when changing page size
      }));
    },
    []
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <ErrorMessage 
        message={error instanceof Error ? error.message : 'Failed to load SIM cards'}
        onRetry={() => window.location.reload()}
      />
    );
  }

  if (!data || data.sims.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-500 dark:text-gray-400 text-lg mb-4">
          {searchParams.q ? 'No SIM cards found matching your search.' : 'No SIM cards available.'}
        </div>
        {searchParams.q && (
          <button
            onClick={() => handleSearch('')}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Clear search
          </button>
        )}
      </div>
    );
  }

  const getSortIcon = (field: string) => {
    const currentSort = searchParams.sort || '';
    if (currentSort === field) return '↑';
    if (currentSort === `-${field}`) return '↓';
    return '';
  };

  // Helper functions for mobile card display
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Enabled':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'Disabled':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const formatDataUsage = (volume?: number, totalVolume?: number) => {
    if (volume === undefined && totalVolume === undefined) return 'N/A';

    const formatBytes = (bytes?: number) => {
      if (bytes === undefined || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const usedStr = formatBytes(volume);
    const totalStr = formatBytes(totalVolume);
    return `${usedStr} / ${totalStr}`;
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString();
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
          SIM Card Management
        </h1>
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            {isFetching && <LoadingSpinner size="small" />}
            <span className="whitespace-nowrap">{data.total_count} SIMs</span>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 min-h-[44px] transition-colors"
            title="Refresh SIM data"
          >
            <svg
              className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            <span className="hidden sm:inline ml-2">{isFetching ? 'Refreshing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar
        value={searchParams.q || ''}
        onChange={handleSearch}
        placeholder="Search by ICCID, IMSI, or label..."
      />

      {/* Table Controls */}
      <div className="flex flex-col xs:flex-row xs:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-700 dark:text-gray-300">Per page:</span>
          <select
            value={searchParams.pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-800 rounded-md px-2 py-1 text-sm min-h-[36px]"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400">
          <span className="hidden sm:inline">Showing </span>
          {((searchParams.page || 1) - 1) * (searchParams.pageSize || 25) + 1}-
          {Math.min((searchParams.page || 1) * (searchParams.pageSize || 25), data.total_count)}
          <span className="hidden xs:inline"> of {data.total_count}</span>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {data.sims.map((sim) => (
          <MobileCard
            key={sim.iccid}
            title={sim.iccid}
            subtitle={sim.label || 'No label'}
            status={
              <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getStatusColor(sim.status)}`}>
                {sim.status}
              </span>
            }
            fields={[
              { label: 'IP', value: sim.ip_address || 'Not assigned' },
              { label: 'Data', value: formatDataUsage(sim.volume, sim.total_volume) },
              { label: 'Expiry', value: formatDate(sim.expiry_date) },
              ...(sim.imei ? [{ label: 'IMEI', value: sim.imei }] : []),
            ]}
            onClick={() => navigate(`/sim/${sim.iccid}`)}
            showChevron
          />
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white dark:bg-gray-800 shadow overflow-hidden rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('iccid')}
                >
                  ICCID {getSortIcon('iccid')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Label
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('ip_address')}
                >
                  IP Address {getSortIcon('ip_address')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Data Usage
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Cost
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Expiry
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                  onClick={() => handleSort('imei')}
                >
                  IMEI {getSortIcon('imei')}
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {data.sims.map((sim) => (
                <SimCardItem
                  key={sim.iccid}
                  sim={sim}
                  onSelect={onSelectSim}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={searchParams.page || 1}
        totalPages={data.total_pages}
        totalItems={data.total_count}
        pageSize={searchParams.pageSize || 25}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default SimCardList;